// File: src/components/CustomDrawer.tsx
import React from 'react';
import {
  IconButton,
  Box, Drawer, Typography, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import Tooltip from '@mui/material/Tooltip';

interface CustomDrawerProps {
  title: string;
  icon: React.ReactNode;
  anchor: 'top' | 'right' | 'bottom' | 'left';
  open: boolean;
  onClose: () => void;
  pinned?: boolean;
  onTogglePinned?: () => void;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  width?: string;
  minWidth?: string;
  height?: string;
  minHeight?: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  children: React.ReactNode;
  keepChildrenMounted?: boolean;
}

const CustomDrawer: React.FC<CustomDrawerProps> = ({
  title,
  icon,
  anchor,
  open,
  onClose,
  pinned = null,
  onTogglePinned = () => null,
  fullscreen = null,
  onToggleFullscreen = () => null,
  width = "100vw",
  minWidth = "100vw",
  height = "100vh",
  minHeight = "100vh",
  top = null,
  bottom = null,
  left = null,
  right = null,
  children,
  keepChildrenMounted = false
}) => {
  if (!open && !keepChildrenMounted) return null;
  const topOffset = top ?? '0px';
  const bottomOffset = bottom ?? '0px';
  const leftOffset = left ?? '0px';
  const rightOffset = right ?? '0px';

  const drawerWidth = fullscreen
    ? `calc(100vw - ${leftOffset} - ${rightOffset})`
    : `max(${width}, ${minWidth})`;
  const drawerHeight = fullscreen
    ? `calc(100vh - ${topOffset} - ${bottomOffset})`
    : `max(${height}, ${minHeight})`;

  const backdropStyles =
    anchor === 'right' ?
      {
        left: leftOffset,
        right: `calc(${drawerWidth} + ${rightOffset})`,
        top: topOffset,
        bottom: bottomOffset,
        width: `calc(100vw - ${drawerWidth} - ${rightOffset} - ${leftOffset})`,
        height: drawerHeight
      }
    : anchor === 'left' ?
      {
        left: `calc(${drawerWidth} + ${leftOffset})`,
        right: rightOffset,
        top: topOffset,
        bottom: bottomOffset,
        width: `calc(100vw - ${drawerWidth} - ${rightOffset} - ${leftOffset})`,
        height: drawerHeight
      }
    : anchor === 'bottom' ?
      {
        left: leftOffset,
        right: rightOffset,
        top: topOffset,
        bottom: `calc(${drawerHeight} + ${bottomOffset})`,
        width: drawerWidth,
        height: `calc(100vh - ${drawerHeight} - ${topOffset} - ${bottomOffset})`
      }
    : anchor === 'top' ?
      {
        left: leftOffset,
        right: rightOffset,
        top: `calc(${drawerHeight} + ${topOffset})`,
        bottom: bottomOffset,
        width: drawerWidth,
        height: `calc(100vh - ${drawerHeight} - ${topOffset} - ${bottomOffset})`
      }
    : {};

  const content = (
    <Box className="custom-drawer-paper" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon}
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {title}
          </Typography>
        </Box>
        <Box>
          { pinned !== null ?
            <Tooltip title={pinned ? `Unpin ${title}` : `Pin ${title}`}>
              <IconButton
                onClick={onTogglePinned}
                aria-label={pinned ? `Unpin ${title}` : `Pin ${title}`}
              >
                {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            </Tooltip> : <></>
          }
          { fullscreen !== null ?
            <Tooltip title={fullscreen ? `Shrink ${title}` : `Expand ${title}`}>
              <IconButton
                onClick={onToggleFullscreen}
                aria-label={fullscreen ? `Shrink ${title}` : `Expand ${title}`}
              >
                {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip> : <></>
          }
          <Tooltip title={`Close ${title}`}>
            <IconButton
              onClick={onClose}
              aria-label={`Close ${title}`}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {children}
      </Box>
    </Box>
  );

  return pinned && !fullscreen ? (
    <Box
      sx={{
        position: 'absolute',
        pointerEvents: open ? 'auto' : 'none',
        visibility: open ? 'visible' : 'hidden',
        top: top,
        bottom: bottom,
        left: left,
        right: right,
        width:     fullscreen ? `calc(100vw - ${left ?? '0px'} - ${right ?? '0px'})` : width,
        minWidth:  fullscreen ? `calc(100vw - ${left ?? '0px'} - ${right ?? '0px'})` : minWidth,
        height:    fullscreen ? `calc(100vh - ${top ?? '0px'} - ${bottom ?? '0px'})` : height,
        minHeight: fullscreen ? `calc(100vh - ${top ?? '0px'} - ${bottom ?? '0px'})` : minHeight,
        p: 2,
        transition: 'height 0.3s',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        bgcolor: 'background.paper',
        boxShadow: 1,
        zIndex: theme => theme.zIndex.drawer - 1
      }}
    >
      {content}
    </Box>
  ) : (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      hideBackdrop={Boolean(fullscreen)}
      variant="temporary"
      ModalProps={{
        keepMounted: true,
        hideBackdrop: Boolean(fullscreen),
        disableAutoFocus: true,
        disableEnforceFocus: true,
        BackdropProps: {
          sx: backdropStyles
        }
      }}
      PaperProps={{
        className: 'custom-drawer-paper',
        sx: {
          position: 'absolute',
          pointerEvents: 'auto',
          width:     drawerWidth,
          minWidth:  drawerWidth,
          height:    drawerHeight,
          minHeight: drawerHeight,
          p: 2,
          transition: 'height 0.3s',
          borderTopLeftRadius: anchor in ['bottom', 'right'] ? 8 : 0,
          borderTopRightRadius: anchor in ['bottom', 'left'] ? 8 : 0,
          borderBottomLeftRadius: anchor in ['top', 'right'] ? 8 : 0,
          borderBottomRightRadius: anchor in ['top', 'left'] ? 8 : 0,
          bgcolor: 'background.paper',
          boxShadow: 1,
          zIndex: theme => theme.zIndex.drawer - 1
        }
      }}
      sx={{
        marginTop: top,
        marginMap: bottom,
        marginLeft: left,
        marginRight: right,
      }}
    >
      {content}
    </Drawer>
  );
};

export default CustomDrawer;
